/**
 * Reading Room — Trivia renderer.
 *
 * Consumes the variant-agnostic engine at `games/trivia/engine.ts` via
 * `useTriviaEngine`. Owns all the visual/motion/sound choices: per-category
 * atmosphere, paper-on-desk card, warm-halo + paper-stamp celebration,
 * Reading Room SFX, motion vocabulary.
 *
 * Other variants supply their own renderer; the engine is shared.
 */
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { isEmbedded } from '@/host/isEmbedded';
import { easing, timings, triggerHaptic, useCountUp } from '@/juice';
import { AtmosphereSurface, buildSrcSet, variantOf } from '@/juice/RevealedImage';
import { usePreferences } from '@/preferences';
import {
  averageReactionTime,
  useTriviaEngine,
  type TriviaMode,
  type TriviaTrialResult,
} from '@/games/trivia/engine';
import { isTriviaCategorySlug, type TriviaCategorySlug } from '@/games/trivia/categoriesSlug';
import type { TriviaQuestion } from '@/games/trivia/bank/index';
import type { DifficultyEngine, GameSession } from '@rendever/games-sdk';

import { Affirmation } from './Affirmation';
import { CategoryMotif } from './CategoryMotif';
import { getCategory, nextCategorySlug, type CategoryMeta } from './categories';
import { questionSpring, optionSpring, OPTION_STAGGER_S } from './motion';
import { sound } from './sound';

import './Trivia.css';

export interface ReadingRoomTriviaProps {
  session: GameSession;
  difficulty: DifficultyEngine;
  initialCategorySlug?: TriviaCategorySlug;
}

export function Trivia({
  session,
  difficulty,
  initialCategorySlug,
}: ReadingRoomTriviaProps): React.JSX.Element {
  const reduced = useReducedMotion();
  const { theme } = usePreferences();

  const initialFromUrl: TriviaCategorySlug = useMemo(() => {
    if (initialCategorySlug) return initialCategorySlug;
    if (typeof window === 'undefined') return 'music-60s';
    const p = new URLSearchParams(window.location.search);
    const c = p.get('category');
    if (isTriviaCategorySlug(c)) return c;
    return 'music-60s';
  }, [initialCategorySlug]);

  const modeFromUrl: TriviaMode = useMemo(() => {
    if (typeof window === 'undefined') return 'daily';
    const p = new URLSearchParams(window.location.search);
    return p.get('mode') === 'practice' ? 'practice' : 'daily';
  }, []);

  const { state, actions } = useTriviaEngine({
    session,
    difficulty,
    initialCategorySlug: initialFromUrl,
    mode: modeFromUrl,
    nextCategorySlug,
  });

  const category = getCategory(state.categorySlug);

  // ---------------------------------------------------------------------------
  // Action wrappers — same as engine actions but layered with variant-local
  // SFX / haptics. The engine itself is sound-free so other variants can pair
  // it with their own palette.
  // ---------------------------------------------------------------------------

  const handleStart = useCallback((): void => {
    sound.unlock();
    sound.play('whoosh');
    triggerHaptic('light');
    actions.start();
  }, [actions]);

  const handleAnswer = useCallback(
    (index: number): void => {
      sound.play('select');
      triggerHaptic('medium');
      actions.answer(index);
    },
    [actions],
  );

  // Mirror sound on the engine's transition into reveal via an effect.
  const previousPhase = useRef(state.phase);
  useEffect(() => {
    if (previousPhase.current !== state.phase) {
      if (state.phase === 'reveal') {
        const trial = state.results[state.results.length - 1];
        if (trial?.correct) {
          sound.play('correct');
          triggerHaptic('success');
        } else if (trial) {
          sound.play('incorrect-soft');
          triggerHaptic('light');
        }
      }
      previousPhase.current = state.phase;
    }
  }, [state.phase, state.results]);

  const handleNext = useCallback((): void => {
    sound.play('tap');
    triggerHaptic('light');
    actions.next();
  }, [actions]);

  const handleAnotherRound = useCallback((): void => {
    sound.play('tap');
    triggerHaptic('light');
    actions.anotherRound();
  }, [actions]);

  const handleBackToGames = useCallback((): void => {
    sound.play('tap-soft');
    triggerHaptic('light');
    void actions.end();
  }, [actions]);

  // ---------------------------------------------------------------------------
  // Stage background — the category atmosphere stays mounted across phases.
  // ---------------------------------------------------------------------------
  const bgSrc = theme === 'dark' ? category.backgroundDark : category.backgroundLight;

  return (
    <div
      className="trivia-room"
      data-category={state.categorySlug}
      data-theme={theme}
      style={
        {
          '--cat-accent': `var(${category.accentVar})`,
        } as React.CSSProperties
      }
    >
      <div className="trivia-room__bg" aria-hidden="true">
        <AtmosphereSurface
          src={bgSrc}
          avifSrc={variantOf(bgSrc, 'avif')}
          webpSrc={variantOf(bgSrc, 'webp')}
          avifSrcSet={buildSrcSet(bgSrc, 'avif', [430, 860, 1366])}
          webpSrcSet={buildSrcSet(bgSrc, 'webp', [430, 860, 1366])}
          sizes="100vw"
          eager
        />
      </div>
      <div className="trivia-room__scrim" aria-hidden="true" />

      {/* Single AnimatePresence wrapping all three phases. mode="wait" lets
       * the exiting card finish before the next mounts — without this, the
       * done→intro transition placed both cards side-by-side in the flex
       * stage. */}
      <AnimatePresence mode="wait" initial={false}>
        {state.phase === 'intro' && (
          <IntroCard
            key={`intro-${state.cardKey}`}
            category={category}
            mode={state.mode}
            onStart={handleStart}
            reduced={reduced ?? false}
          />
        )}
        {state.phase !== 'intro' && state.phase !== 'done' && state.currentQuestion && (
          <QuestionStage
            key={`q-${state.cardKey}-${state.currentQuestion.id}`}
            category={category}
            mode={state.mode}
            question={state.currentQuestion}
            questionIndex={state.questionIndex}
            totalQuestions={state.questionsPerRound}
            chosenIndex={state.chosenIndex}
            revealing={state.phase === 'reveal'}
            revealReady={state.revealReady}
            score={state.score}
            streak={state.streak}
            correctSoFar={state.correctSoFar}
            results={state.results}
            onAnswer={handleAnswer}
            onNext={handleNext}
            isLast={state.isLastQuestion}
            reduced={reduced ?? false}
          />
        )}
        {state.phase === 'done' && (
          <ClosingAffirmation
            key={`done-${state.cardKey}`}
            category={category}
            mode={state.mode}
            correctSoFar={state.correctSoFar}
            score={state.score}
            results={state.results}
            reduced={reduced ?? false}
            onAnotherRound={handleAnotherRound}
            onBackToGames={handleBackToGames}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Intro card
// ---------------------------------------------------------------------------

interface IntroCardProps {
  category: CategoryMeta;
  mode: TriviaMode;
  onStart: () => void;
  reduced: boolean;
}

function IntroCard({ category, mode, onStart, reduced }: IntroCardProps) {
  const spring = reduced
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 220, damping: 22, mass: 0.9 };
  return (
    <motion.div
      className="trivia-stage"
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 36, scale: 0.97 }}
      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
      exit={
        reduced
          ? { opacity: 0, transition: { duration: 0 } }
          : {
              opacity: 0,
              y: -12,
              transition: { duration: 0.18, ease: [0.33, 1, 0.68, 1] },
            }
      }
      transition={spring}
    >
      <div className="trivia-card trivia-card--intro" role="region" aria-label="Trivia intro">
        <p className="trivia-card__intro-tag">
          {mode === 'daily' ? 'Daily Trivia' : 'Trivia Practice'}
        </p>
        <motion.div
          className="trivia-card__motif"
          aria-hidden="true"
          initial={reduced ? false : { opacity: 0, scale: 0.72, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={reduced ? { duration: 0 } : { ...spring, delay: 0.12 }}
        >
          <CategoryMotif kind={category.motifKind} size={120} />
        </motion.div>
        <h2 className="trivia-card__title">{category.title}</h2>
        <p className="trivia-card__intro-body">
          {mode === 'daily'
            ? "Today's five questions, no clock. Take your time."
            : 'Five fresh practice questions, no clock. Take your time.'}
        </p>
        <ul className="trivia-card__round-details" aria-label="Round details">
          <li>
            <strong>5</strong> questions
          </li>
          <li>
            <strong>No</strong> timer
          </li>
          <li>
            <strong>Fresh</strong> mix
          </li>
        </ul>
        <motion.button
          type="button"
          className="trivia-cta"
          onClick={onStart}
          autoFocus
          whileTap={reduced ? {} : { scale: 0.97 }}
        >
          <span className="trivia-cta__label">Begin round</span>
          <span className="trivia-cta__icon" aria-hidden="true">
            →
          </span>
        </motion.button>
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Question stage
// ---------------------------------------------------------------------------

interface QuestionStageProps {
  category: CategoryMeta;
  mode: TriviaMode;
  question: TriviaQuestion;
  questionIndex: number;
  totalQuestions: number;
  chosenIndex: number | null;
  revealing: boolean;
  revealReady: boolean;
  score: number;
  streak: number;
  correctSoFar: number;
  results: ReadonlyArray<TriviaTrialResult>;
  onAnswer: (index: number) => void;
  onNext: () => void;
  isLast: boolean;
  reduced: boolean;
}

function QuestionStage({
  category,
  mode,
  question,
  questionIndex,
  totalQuestions,
  chosenIndex,
  revealing,
  revealReady,
  score,
  streak,
  correctSoFar,
  results,
  onAnswer,
  onNext,
  isLast,
  reduced,
}: QuestionStageProps) {
  const wasCorrect = chosenIndex !== null && chosenIndex === question.correctIndex;
  const countUpEase = streak >= 2 ? easing.outBack.tuple : easing.outQuart.tuple;
  const animatedScore = useCountUp({
    to: score,
    durationMs: streak >= 2 ? timings.celebrate : timings.base,
    ease: countUpEase,
  });

  // Focus next button when reveal lands and the hold pause has elapsed.
  const nextBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (revealReady && nextBtnRef.current) {
      nextBtnRef.current.focus();
    }
  }, [revealReady]);

  const cardSpring_local = reduced ? { duration: 0 } : questionSpring;
  const stagger = reduced ? 0 : OPTION_STAGGER_S;
  const optSpring = reduced ? { duration: 0 } : optionSpring;

  return (
    <motion.div
      className="trivia-stage"
      initial={reduced ? false : { opacity: 0, y: 70, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={
        reduced
          ? { opacity: 0, transition: { duration: 0 } }
          : {
              opacity: 0,
              y: -24,
              transition: { duration: 0.3, ease: [0.33, 1, 0.68, 1] },
            }
      }
      transition={cardSpring_local}
    >
      <div
        className="trivia-card trivia-card--question"
        role="region"
        aria-label={`Question ${questionIndex + 1} of ${totalQuestions}`}
      >
        <header className="trivia-card__header">
          <div className="trivia-card__progress" aria-live="polite">
            <span className="trivia-card__progress-label">Question</span>
            <span className="trivia-card__progress-value">
              <strong>{questionIndex + 1}</strong>
              <span aria-hidden="true"> / </span>
              <span>{totalQuestions}</span>
            </span>
          </div>
          <div className="trivia-card__category">
            <span className="trivia-card__motif-mini" aria-hidden="true">
              <CategoryMotif kind={category.motifKind} size={28} />
            </span>
            <span className="trivia-card__category-stack">
              <span className="trivia-card__category-tag">
                {mode === 'daily' ? 'Daily Trivia' : 'Practice'}
              </span>
              <span className="trivia-card__category-name">{category.title}</span>
            </span>
          </div>
          <div className="trivia-card__score" aria-live="polite">
            <span className="trivia-card__score-label">Score</span>
            <span className="trivia-card__score-value">{animatedScore}</span>
          </div>
        </header>

        <RoundProgress
          questionIndex={questionIndex}
          totalQuestions={totalQuestions}
          results={results}
        />

        <h2 className="trivia-card__prompt">{question.prompt}</h2>

        <AnimatePresence>
          {revealing && (
            <motion.div
              className={`trivia-feedback ${wasCorrect ? 'trivia-feedback--good' : 'trivia-feedback--soft'}`}
              aria-live="polite"
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
              animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduced ? 0 : 0.3, ease: [0.25, 1, 0.5, 1] }}
            >
              <div className="trivia-feedback__heading">
                <strong>{wasCorrect ? "That's right." : 'Good try.'}</strong>
                {wasCorrect && streak >= 2 ? (
                  <span className="trivia-feedback__streak">{streak} in a row</span>
                ) : null}
              </div>
              {question.blurb ? <p>{question.blurb}</p> : null}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="trivia-options" role="list">
          {question.options.map((option, idx) => (
            <motion.div
              key={`${question.id}-${idx}`}
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12 }}
              animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
              transition={{ ...optSpring, delay: stagger * idx }}
            >
              <AnswerButton
                label={option}
                index={idx}
                chosenIndex={chosenIndex}
                correctIndex={question.correctIndex}
                revealing={revealing}
                disabled={chosenIndex !== null}
                onClick={() => onAnswer(idx)}
                reduced={reduced}
              />
            </motion.div>
          ))}
        </div>

        <div className="trivia-card__footer">
          {revealing ? (
            <motion.div
              className="trivia-card__next-wrap"
              initial={{ opacity: 0 }}
              animate={{ opacity: revealReady ? 1 : 0.4 }}
              transition={{ duration: 0.25 }}
            >
              <motion.button
                ref={nextBtnRef}
                type="button"
                className="trivia-cta"
                onClick={onNext}
                disabled={!revealReady}
                whileTap={reduced || !revealReady ? {} : { scale: 0.97 }}
              >
                <span className="trivia-cta__label">
                  {isLast ? 'See how I did' : 'Next question'}
                </span>
                <span className="trivia-cta__icon" aria-hidden="true">
                  →
                </span>
              </motion.button>
            </motion.div>
          ) : (
            <div className="trivia-card__hint">
              {correctSoFar > 0
                ? `${correctSoFar} right so far. Take your time.`
                : "There's no clock. Read it twice if you like."}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function RoundProgress({
  questionIndex,
  totalQuestions,
  results,
}: {
  questionIndex: number;
  totalQuestions: number;
  results: ReadonlyArray<TriviaTrialResult>;
}) {
  return (
    <div
      className="trivia-round-progress"
      role="progressbar"
      aria-label="Round progress"
      aria-valuemin={1}
      aria-valuemax={totalQuestions}
      aria-valuenow={questionIndex + 1}
    >
      {Array.from({ length: totalQuestions }, (_, index) => {
        const result = results[index];
        const state = result
          ? result.correct
            ? 'correct'
            : 'miss'
          : index === questionIndex
            ? 'current'
            : 'upcoming';
        return <span key={index} data-state={state} aria-hidden="true" />;
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Answer button
// ---------------------------------------------------------------------------

interface AnswerButtonProps {
  label: string;
  index: number;
  chosenIndex: number | null;
  correctIndex: number;
  revealing: boolean;
  disabled: boolean;
  onClick: () => void;
  reduced: boolean;
}

function AnswerButton({
  label,
  index,
  chosenIndex,
  correctIndex,
  revealing,
  disabled,
  onClick,
  reduced,
}: AnswerButtonProps) {
  const isChosen = chosenIndex === index;
  const isCorrect = index === correctIndex;
  const wrongChosen = revealing && chosenIndex !== null && chosenIndex !== correctIndex;

  let state:
    | 'idle'
    | 'chosen'
    | 'reveal-correct'
    | 'reveal-wrong'
    | 'reveal-other-correct'
    | 'reveal-idle';
  if (revealing) {
    if (isChosen && isCorrect) state = 'reveal-correct';
    else if (isChosen && !isCorrect) state = 'reveal-wrong';
    else if (!isChosen && isCorrect && wrongChosen) state = 'reveal-other-correct';
    else state = 'reveal-idle';
  } else if (isChosen) {
    state = 'chosen';
  } else {
    state = 'idle';
  }

  const letter = String.fromCharCode(65 + index);

  return (
    <motion.button
      type="button"
      className={`trivia-option trivia-option--${state}`}
      data-state={state}
      data-testid={`trivia-option-${index}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={isChosen}
      aria-label={`${letter}. ${label}`}
      whileTap={disabled || reduced ? {} : { scale: 0.97 }}
      animate={
        reduced
          ? { y: 0, scale: 1, opacity: 1 }
          : state === 'reveal-correct'
            ? { y: -8, scale: 1.01 }
            : state === 'reveal-wrong'
              ? { opacity: 0.42 }
              : state === 'reveal-idle'
                ? { opacity: 0.55 }
                : { y: 0, scale: 1, opacity: 1 }
      }
      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
    >
      <span className="trivia-option__letter" aria-hidden="true">
        {letter}
      </span>
      <span className="trivia-option__label">{label}</span>
      {state === 'reveal-correct' && <CorrectCheck reduced={reduced} />}
      {state === 'reveal-other-correct' && (
        <span className="trivia-option__nudge" aria-hidden="true">
          → This one
        </span>
      )}
    </motion.button>
  );
}

// ---------------------------------------------------------------------------
// Inline correct-answer check
// ---------------------------------------------------------------------------
//
// Rendered in the third grid column of the chosen-correct row. Anchored to
// the row the user just clicked — exactly where their eye is — and sits in
// the same slot the wrong-answer "→ This one" nudge uses, which is empty
// for this state. Sage green so it always reads as "correct" regardless of
// category accent. Draws in via SVG pathLength + a soft sage halo behind.
// All GPU-cheap: opacity / transform / path-length only.

function CorrectCheck({ reduced }: { reduced: boolean }): React.JSX.Element {
  return (
    <span className="trivia-option__check" aria-hidden="true">
      <motion.span
        className="trivia-option__check-halo"
        initial={reduced ? { opacity: 0.55, scale: 1 } : { opacity: 0, scale: 0.7 }}
        animate={
          reduced
            ? { opacity: 0.55, scale: 1 }
            : { opacity: [0, 0.6, 0.55, 0.45], scale: [0.7, 1, 1.04, 1.02] }
        }
        transition={
          reduced
            ? { duration: 0 }
            : {
                duration: 0.55,
                times: [0, 0.3, 0.7, 1],
                ease: [0.25, 1, 0.5, 1],
              }
        }
      />
      <motion.svg
        className="trivia-option__check-svg"
        viewBox="0 0 56 56"
        aria-hidden="true"
        initial={reduced ? { scale: 1 } : { scale: 0.86 }}
        animate={reduced ? { scale: 1 } : { scale: [0.86, 1.05, 1] }}
        transition={
          reduced
            ? { duration: 0 }
            : { duration: 0.45, times: [0, 0.7, 1], ease: [0.34, 1.56, 0.64, 1] }
        }
      >
        <motion.path
          d="M14 30 L24 40 L42 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="5.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reduced ? { pathLength: 1 } : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={
            reduced ? { duration: 0 } : { duration: 0.36, delay: 0.08, ease: [0.65, 0, 0.35, 1] }
          }
        />
      </motion.svg>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Closing Affirmation
// ---------------------------------------------------------------------------

interface ClosingAffirmationProps {
  category: CategoryMeta;
  mode: TriviaMode;
  correctSoFar: number;
  score: number;
  results: ReadonlyArray<TriviaTrialResult>;
  reduced: boolean;
  onAnotherRound: () => void;
  onBackToGames: () => void;
}

function ClosingAffirmation({
  category,
  mode,
  correctSoFar,
  score,
  results,
  reduced,
  onAnotherRound,
  onBackToGames,
}: ClosingAffirmationProps) {
  const totalQuestions = results.length || 5;

  // Sound and motion are separate preferences. Keep the audible bookend when
  // motion is reduced, and let the second cue grow gently with the score.
  useEffect(() => {
    sound.play('page-turn');
    const finishSound =
      correctSoFar >= 4 ? 'celebrate-big' : correctSoFar >= 2 ? 'celebrate' : 'chime';
    const t = window.setTimeout(() => sound.play(finishSound), 320);
    return () => window.clearTimeout(t);
  }, [correctSoFar]);

  const avgRtMs = averageReactionTime(results);
  const avgTime = formatAverageReactionTime(avgRtMs);

  const closingSpring = reduced
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 220, damping: 23, mass: 1.0 };

  return (
    <motion.div
      className="trivia-stage trivia-stage--end"
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 40 }}
      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={
        reduced
          ? { opacity: 0, transition: { duration: 0 } }
          : { opacity: 0, transition: { duration: 0.3 } }
      }
      transition={closingSpring}
    >
      <Affirmation
        eyebrow={`${category.title} · ${mode === 'daily' ? 'Daily round' : 'Practice round'}`}
        headline={pickHeadline(correctSoFar, totalQuestions)}
        subhead={pickSubhead(correctSoFar, category.title, totalQuestions)}
        glyph={<CategoryMotif kind={category.motifKind} size={56} />}
        stats={[
          { label: 'Correct', value: `${correctSoFar} / ${totalQuestions}` },
          { label: 'Final score', value: <FinalScore target={score} /> },
          { label: 'Avg. pace', value: avgTime },
        ]}
        primaryAction={{
          label: mode === 'daily' ? 'Next daily category' : 'Play next category',
          onClick: onAnotherRound,
        }}
        /* Suppressed when embedded: the host owns the way out, and with the
           hub navigation gone this button would end the session and leave the
           resident looking at the same screen. */
        secondaryAction={
          isEmbedded() ? undefined : { label: 'Back to games', onClick: onBackToGames }
        }
      >
        <RoundRecap results={results} reduced={reduced} />
      </Affirmation>
    </motion.div>
  );
}

function RoundRecap({
  results,
  reduced,
}: {
  results: ReadonlyArray<TriviaTrialResult>;
  reduced: boolean;
}) {
  return (
    <div className="trivia-round-recap">
      <span className="trivia-round-recap__label">Your five answers</span>
      <ol className="trivia-round-recap__marks">
        {results.map((result, index) => (
          <motion.li
            key={result.questionId}
            data-result={result.correct ? 'correct' : 'miss'}
            aria-label={`Question ${index + 1}: ${result.correct ? 'correct' : 'not quite'}`}
            initial={reduced ? false : { opacity: 0, y: 10, scale: 0.78 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={
              reduced
                ? { duration: 0 }
                : {
                    type: 'spring',
                    stiffness: 330,
                    damping: 18,
                    delay: 0.22 + index * 0.08,
                  }
            }
          >
            <span aria-hidden="true">{result.correct ? '✓' : index + 1}</span>
          </motion.li>
        ))}
      </ol>
    </div>
  );
}

function FinalScore({ target }: { target: number }) {
  const value = useCountUp({
    to: target,
    durationMs: timings.celebrate,
    ease: easing.outBack.tuple,
  });
  return <>{value}</>;
}

// ---------------------------------------------------------------------------
// Scorecard copy
// ---------------------------------------------------------------------------

function pickHeadline(correctCount: number, totalQuestions: number): string {
  if (correctCount === totalQuestions) return 'A clean sweep!';
  if (correctCount >= totalQuestions - 1) return 'You know your stuff.';
  if (correctCount >= Math.ceil(totalQuestions / 2)) return 'Nicely played.';
  if (correctCount > 0) return 'One for the win column.';
  return 'Round one is in the books.';
}

function pickSubhead(correctCount: number, category: string, totalQuestions: number): string {
  if (correctCount === totalQuestions) {
    return `${category} never stood a chance. Every answer landed.`;
  }
  if (correctCount >= totalQuestions - 1) {
    return `${correctCount} out of ${totalQuestions} on ${category}. That was a strong finish.`;
  }
  if (correctCount >= 2) {
    return `${correctCount} right on ${category}, with a fresh category ready to go.`;
  }
  if (correctCount === 1) {
    return `${category} brought a tricky set. You still put one on the board.`;
  }
  return `${category} brought the deep cuts this time. A fresh set is ready when you are.`;
}

function formatAverageReactionTime(ms: number): string {
  if (ms <= 0) return '—';
  if (ms < 1000) return `${Math.max(1, Math.round(ms / 100) * 100)}ms`;
  const seconds = ms / 1000;
  return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`;
}
